import { Injectable } from '@angular/core';
import { LevelOption } from '../../components/reports/edit-report/dual-list-picker.component';
import { ReportField, ReportLevelType } from '../../models/api/reports/report';

export type ReserveStatusType = 'N' | 'ACCEPTED_RESERVES' | 'PENDING_RESERVES';

interface DefaultFieldConfig {
  fieldKey: string; // tableAlias + columnName
  displayName: string;
}

@Injectable({ providedIn: 'root' })
export class ReportLevelService {
  readonly AD_HOC_CLAIM_REPORT_ID = 'SRS_ADHOC';
  readonly AD_HOC_PAYMENT_REPORT_ID = 'SRS_ADHOC_PAYMENT';
  readonly AD_HOC_RESERVE_REPORT_ID = 'SRS_ADHOC_RESERVE';

  readonly levelOptions: LevelOption[] = [
    { label: 'Please Select...', value: 'N' },
    { label: 'Payment Level', value: 'PAYMENT' },
    { label: 'Payment Level by Claim', value: 'PAYMENT_LEVEL_BY_CLAIM' },
    { label: 'Allocation Level', value: 'ALLOCATION' },
  ];

  readonly reserveStatusOptions: LevelOption[] = [
    { label: 'Please Select', value: 'N' },
    { label: 'Accepted Reserves', value: 'ACCEPTED_RESERVES' },
    { label: 'Pending Reserves', value: 'PENDING_RESERVES' },
  ];

  // Payment level defaults (cumulative: ALLOCATION includes all)
  private readonly paymentLevelDefaults: DefaultFieldConfig[] = [
    {
      fieldKey: 'claim_allocationPAYMENT_UNIQUE_KEY',
      displayName: 'Payment Unique Key',
    },
    {
      fieldKey: 'claim_allocationTOTAL_AMOUNT_BY_PAYMENT',
      displayName: 'Total Amount By Payment',
    },
  ];

  private readonly paymentByClaimLevelDefaults: DefaultFieldConfig[] = [
    { fieldKey: 'claimFORMATTED_FILE_NUMBER', displayName: 'File Number' },
    { fieldKey: 'claimVENDOR_ID', displayName: 'Intake Vendor ID' },
    {
      fieldKey: 'claim_allocationTOTAL_AMOUNT_BY_PAYMENT_AND_CLAIM',
      displayName: 'Total Amount By Payment and Claim',
    },
  ];

  private readonly allocationLevelDefaults: DefaultFieldConfig[] = [
    { fieldKey: 'claim_allocationALLOCATE_UID', displayName: 'Allocate UID' },
    { fieldKey: 'claim_allocationAMOUNT', displayName: 'Allocation Amount' },
    {
      fieldKey: 'claim_allocationPAYMENT_TYPE_DESCRIPTION',
      displayName: 'Payment Type Description',
    },
  ];

  // Mandatory fields per level (cannot be removed by user)
  private readonly paymentLevelMandatory: string[] = ['Payment Unique Key'];

  private readonly paymentByClaimLevelMandatory: string[] = [
    'Payment Unique Key',
    'File Number',
    'Intake Vendor ID',
  ];

  private readonly allocationLevelMandatory: string[] = [
    'Payment Unique Key',
    'File Number',
    'Intake Vendor ID',
    'Allocate UID',
  ];

  // Reserve report config - unique fields are mandatory and status-specific
  private readonly uniqueAcceptedFields: string[] = [
    'Reserve Number',
    'Date of Reserve Change (Accepted)',
    'Reserve Created By (Accepted)',
    'Reserve Comment (Accepted)',
    'Changed Ind/Loss Reserve',
    'Changed Medical Reserve',
    'Changed Expense Reserve',
    'Changed Total Reserve',
  ];

  private readonly uniquePendingFields: string[] = [
    'Date of Reserve Change (Pending)',
    'Reserve Created By (Pending)',
    'Reserve Comment (Pending)',
    'Pending Ind/Loss Reserve Change',
    'Pending Medical Reserve Change',
    'Pending Expense Reserve Change',
    'Pending Total Reserve Change',
  ];

  // Default fields auto-populated when status selected
  private readonly defaultAcceptedFields: string[] = [
    'Reserve Number',
    'Date of Reserve Change (Accepted)',
    'Reserve Created By (Accepted)',
    'Reserve Comment (Accepted)',
    'Changed Ind/Loss Reserve',
    'Changed Medical Reserve',
    'Changed Expense Reserve',
    'Changed Total Reserve',
    'Claimant Full Name (Last_First)',
    'Date of Loss',
    'Date Claim Opened',
    'State of Loss',
    'State of Jurisdiction',
    'Line Type',
    'Claim Status Description',
    'Claim Total Incurred',
    'Claim Total Paid',
    'Claim Total Future Reserves',
  ];

  private readonly defaultPendingFields: string[] = [
    'Date of Reserve Change (Pending)',
    'Reserve Created By (Pending)',
    'Reserve Comment (Pending)',
    'Pending Ind/Loss Reserve Change',
    'Pending Medical Reserve Change',
    'Pending Expense Reserve Change',
    'Pending Total Reserve Change',
    'Claimant Full Name (Last_First)',
    'Date of Loss',
    'Date Claim Opened',
    'State of Loss',
    'State of Jurisdiction',
    'Line Type',
    'Claim Status Description',
    'Claim Total Incurred',
    'Claim Total Paid',
    'Claim Total Future Reserves',
  ];

  isAdHocClaimReport(reportId: string): boolean {
    return reportId === this.AD_HOC_CLAIM_REPORT_ID;
  }

  isAdHocPaymentReport(reportId: string): boolean {
    return reportId === this.AD_HOC_PAYMENT_REPORT_ID;
  }

  isAdHocReserveReport(reportId: string): boolean {
    return reportId === this.AD_HOC_RESERVE_REPORT_ID;
  }

  /** PAYMENT: only PAYMENT fields. PAYMENT_LEVEL_BY_CLAIM: PAYMENT/CLAIM/PAYMENT_BY_CLAIM. ALLOCATION: all. */
  isFieldVisibleForLevel(field: ReportField, level: ReportLevelType): boolean {
    if (level === 'N') return false;
    if (!field.level) return true;

    switch (level) {
      case 'PAYMENT':
        return field.level === 'PAYMENT';
      case 'PAYMENT_LEVEL_BY_CLAIM':
        return (
          field.level === 'PAYMENT' ||
          field.level === 'CLAIM' ||
          field.level === 'PAYMENT_BY_CLAIM'
        );
      case 'ALLOCATION':
        return true;
      default:
        return true;
    }
  }

  createLevelFilter(level: ReportLevelType): (field: ReportField) => boolean {
    return (field: ReportField) => this.isFieldVisibleForLevel(field, level);
  }

  filterIncompatibleFields(
    selectedFields: ReportField[],
    newLevel: ReportLevelType,
  ): ReportField[] {
    if (newLevel === 'N') return [];
    return selectedFields.filter((field) =>
      this.isFieldVisibleForLevel(field, newLevel),
    );
  }

  getDefaultFieldKeysForLevel(level: ReportLevelType): string[] {
    const keys: string[] = [];
    if (level === 'N') return keys;

    if (
      level === 'PAYMENT' ||
      level === 'PAYMENT_LEVEL_BY_CLAIM' ||
      level === 'ALLOCATION'
    ) {
      keys.push(...this.paymentLevelDefaults.map((f) => f.fieldKey));
    }
    if (level === 'PAYMENT_LEVEL_BY_CLAIM' || level === 'ALLOCATION') {
      keys.push(...this.paymentByClaimLevelDefaults.map((f) => f.fieldKey));
    }
    if (level === 'ALLOCATION') {
      keys.push(...this.allocationLevelDefaults.map((f) => f.fieldKey));
    }

    return keys;
  }

  getDefaultFieldsForLevel(
    availableFields: ReportField[],
    level: ReportLevelType,
  ): ReportField[] {
    const defaultKeys = this.getDefaultFieldKeysForLevel(level);
    if (defaultKeys.length === 0) return [];

    const keySet = new Set(defaultKeys);
    return availableFields.filter((field) => {
      const fieldKey = (field.tableAlias || '') + (field.columnName || '');
      return keySet.has(fieldKey);
    });
  }

  getFieldKey(field: ReportField): string {
    return (field.tableAlias || '') + (field.columnName || '');
  }

  getMandatoryFieldsForLevel(level: ReportLevelType): string[] {
    switch (level) {
      case 'PAYMENT':
        return this.paymentLevelMandatory;
      case 'PAYMENT_LEVEL_BY_CLAIM':
        return this.paymentByClaimLevelMandatory;
      case 'ALLOCATION':
        return this.allocationLevelMandatory;
      default:
        return [];
    }
  }

  isFieldMandatory(field: ReportField, level: ReportLevelType): boolean {
    const mandatoryFields = this.getMandatoryFieldsForLevel(level);
    return mandatoryFields.includes(field.defaultValue || '');
  }

  // Reserve Report Methods

  /** ACCEPTED: hides pending fields. PENDING: hides accepted fields. */
  isReserveFieldVisibleForStatus(
    field: ReportField,
    status: ReserveStatusType,
  ): boolean {
    if (status === 'N') return true;

    const fieldName = field.defaultValue || '';

    if (status === 'ACCEPTED_RESERVES') {
      return !this.uniquePendingFields.includes(fieldName);
    }
    if (status === 'PENDING_RESERVES') {
      return !this.uniqueAcceptedFields.includes(fieldName);
    }

    return true;
  }

  filterIncompatibleReserveFields(
    selectedFields: ReportField[],
    newStatus: ReserveStatusType,
  ): ReportField[] {
    if (newStatus === 'N') return [];
    return selectedFields.filter((field) =>
      this.isReserveFieldVisibleForStatus(field, newStatus),
    );
  }

  getReserveMandatoryFieldsForStatus(status: ReserveStatusType): string[] {
    if (status === 'ACCEPTED_RESERVES') return this.uniqueAcceptedFields;
    if (status === 'PENDING_RESERVES') return this.uniquePendingFields;
    return [];
  }

  isReserveFieldMandatory(
    field: ReportField,
    status: ReserveStatusType,
  ): boolean {
    const mandatoryFields = this.getReserveMandatoryFieldsForStatus(status);
    return mandatoryFields.includes(field.defaultValue || '');
  }

  getReserveDefaultFieldNamesForStatus(status: ReserveStatusType): string[] {
    if (status === 'ACCEPTED_RESERVES') return this.defaultAcceptedFields;
    if (status === 'PENDING_RESERVES') return this.defaultPendingFields;
    return [];
  }

  getReserveDefaultFieldsForStatus(
    availableFields: ReportField[],
    status: ReserveStatusType,
  ): ReportField[] {
    const defaultNames = this.getReserveDefaultFieldNamesForStatus(status);
    if (defaultNames.length === 0) return [];

    const nameSet = new Set(defaultNames);
    return availableFields.filter((field) =>
      nameSet.has(field.defaultValue || ''),
    );
  }
}
